import {yupResolver} from '@hookform/resolvers/yup';
import {Grid, Stack} from '@mui/material';
import {GridSelectionModel} from '@mui/x-data-grid';
import React, {useEffect, useState} from 'react'
import {FieldValues, useForm} from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useRecoilStateLoadable, useRecoilValue} from 'recoil';
import colors from '../../../assets/theme/base/colors';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import {AppDatepicker} from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import {MDButton} from '../../../Component/Common/Components/MDButton';
import {MDTypography} from '../../../Component/Common/Components/MDTypography';
import {ScorecardMeta} from '../../../Component/Model/Dto/Scorecard/ScorecardMeta';
import {defaultObject} from '../../../Helpers/typescriptHelper';
import {ScorecardAtom} from '../../../State/Scorecard.Atom';
import {ScorecardListState} from '../../../State/ScorecardSelection.Atom';
import {UserDataTable} from '../../Base/UserManagement/UserDataTable';
import {EEUserScorecardDataGrid} from '../../Base/UserManagement/UserScorecardDataColumns';
import {consolidationSchema} from './ConsolidationValidationSchema';
import * as EEScorecardActions from '../../../Component/Common/Constants/EEScorecard.Actions';
import {Reconsolidation} from './Reconsolidation';
import {ConsolidationRules} from "./ConsolidationRules";
import {EEScorecard} from "../../../Component/Model/Dto/EmploymentEquity/EEScorecard";
import {EEScorecardService} from '../../../Component/Api/EmploymentEquity/Scorecard/EEScorecardService';
import useScrollToError from '../../../Hooks/useScrollToError';

const {dark} = colors;

interface SelectedScorecards {
    id: string;
    scorecardName: string;
}

export const Consolidation = () => {

    const location = useLocation<any>();
    const scorecard = useRecoilValue(ScorecardAtom);
    const { control, handleSubmit, formState: { isSubmitting, errors }, setValue, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(consolidationSchema)
    });
    const [showRulesPage, setShowRulesPage] = React.useState(false);

    const [EEUserScorecardData, setEEUserScorecardData] = useRecoilStateLoadable(ScorecardListState);
    const [EEScorecardSelectionModel, setEEScorecardSelectionModel] = React.useState<GridSelectionModel>([]);
    const [SelectedScorecards, setSelectedScorecards] = React.useState<SelectedScorecards[]>([]);
    const [MetaData, setMetaData] = useState<EEScorecard.MetaDto>(defaultObject);
    const [ConsolidationView, setConsolidationView] = React.useState("");


    useEffect(() => {
        if (location.state == null && scorecard.scorecardId != null) {
            setConsolidationView(EEScorecardActions.update);
        }
        else if (location.state.action === EEScorecardActions.create && location.state.scorecardLicenseId) {
            setConsolidationView(EEScorecardActions.create);
        }
    }, [location.state, scorecard.scorecardId]);


    const handleDataSubmit = async (data: FieldValues) => {
        const minimumScorecards = 2;
        if (EEScorecardSelectionModel.length < minimumScorecards) {
            toast.error('Please ensure two or more scorecards are selected.')
            return;
        }

        try {
            const response = await EEScorecardService.ValidateEEConsolidationScorecardName(data.ScorecardName);
            setSelectedScorecards(mapScorecardIdToName(EEScorecardSelectionModel));
            const metaDto: EEScorecard.MetaDto = {
                startDate: data.FromDate,
                endDate: data.ToDate,
                scorecardName: data.ScorecardName,
                scorecardLicenseId: location.state.scorecardLicenseId,
                verificationAgencyName: data.VerificationAgencyName,
                scorecardType: data.ScorecardType
            }
            setMetaData(metaDto);
            setShowRulesPage(true);
        } catch (error) {
            console.log(error);
        }
    }

    const mapScorecardIdToName = (SelectedIds: GridSelectionModel) => {
        let mappedScorecards: SelectedScorecards[] = [];
        SelectedIds.forEach((id) => {
            mappedScorecards.push({
                id: id.toString(),
                scorecardName: EEUserScorecardData.contents.find((scorecard: ScorecardMeta.ScorecardMetaViewModel) => scorecard.ID === id)?.Name
            })
        });
        return mappedScorecards;
    }

    useScrollToError(errors)

    const hideRulesPage = () => {
        setShowRulesPage(false);
    }

    if (ConsolidationView === EEScorecardActions.update) {
        return <Reconsolidation />
    }
    if (showRulesPage) {
        return <ConsolidationRules metaData={MetaData} selectedScorecards={SelectedScorecards} previous={hideRulesPage} />
    }
    return (
        <>
            <form onSubmit={handleSubmit(handleDataSubmit)}>
                <Stack spacing={2}>
                    <Grid container>
                        <Grid item xs={6}>
                            <AppTextInput control={control} name="ScorecardName" label='Scorecard Name'/>
                            <MDTypography>Reporting Period</MDTypography>
                            <AppDatepicker control={control} name="FromDate" label="From Date" inputFormat='yyyy/MM/dd'/>
                            <AppDatepicker control={control} name="ToDate" label="To Date" inputFormat='yyyy/MM/dd' />
                        </Grid>
                    </Grid>
                    <Grid item >
                        <AppAccordion label='Scorecards' expandOnDefault={true} backgroundColor='white' iconFill={dark.main} textColor={dark.main}>
                            <UserDataTable
                                columns={EEUserScorecardDataGrid.columns}
                                isLoading={EEUserScorecardData.state === 'loading'}
                                rows={EEUserScorecardData.contents}
                                selectionModel={EEScorecardSelectionModel}
                                setSelectionModel={setEEScorecardSelectionModel}
                                checkboxSelection={true}
                            />
                        </AppAccordion>
                    </Grid>
                </Stack>

                <Grid container justifyContent='flex-end' spacing={2} pt={2}>
                    <Grid item>
                        <MDButton variant="gradient" color="info" type='submit' isLoading={isSubmitting}>Next</MDButton>
                    </Grid>
                </Grid>
            </form>
        </>
    )
}


